import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Divider,
    CircularProgress
} from '@mui/material';
import { CloseOutlinedIcon, InfoOutlinedIcon } from 'icons/materialIcons';
import TeamIosDarkToggle from 'team/shared/components/button/TeamIosDarkToggle';
import TeamNotificationSnackbar from 'team/shared/components/notification/TeamNotificationSnackbar';
import Modal from 'components/common/Modal';
import { getUserDefaultSettings, updateUserDefaultSettings } from 'apis/authApi';

const TeamMemberSettingsModal = ({ open, onClose, memberData }) => {
    // State for toggles
    const [isFollowUpEmailEnabled, setIsFollowUpEmailEnabled] = useState(false);
    const [isLeadCaptureEnabled, setIsLeadCaptureEnabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [initialized, setInitialized] = useState(false);

    // Notification state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Load settings when modal opens
    useEffect(() => {
        const loadSettings = async () => {
            if (!open || !memberData?.userId || initialized) return;

            try {
                setLoading(true);
                console.log('[TeamMemberSettingsModal] Loading settings for user:', memberData.userId);

                const response = await getUserDefaultSettings(memberData.userId);

                if (response && response.defaultSettings) {
                    const { leadCaptureMode, autoFollowUpIntroEmail } = response.defaultSettings;
                    setIsLeadCaptureEnabled(leadCaptureMode || false);
                    setIsFollowUpEmailEnabled(autoFollowUpIntroEmail !== undefined ? autoFollowUpIntroEmail : true);

                    console.log('[TeamMemberSettingsModal] Settings loaded:', { leadCaptureMode, autoFollowUpIntroEmail });
                }
                setInitialized(true);
            } catch (error) {
                console.error('[TeamMemberSettingsModal] Error loading settings:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to load member settings',
                    severity: 'error'
                });
            } finally {
                setLoading(false);
            }
        };

        loadSettings();
    }, [open, memberData?.userId, initialized]);

    // Reset when modal closes
    useEffect(() => {
        if (!open) {
            setInitialized(false);
        }
    }, [open]);

    // Handler for Follow Up Email toggle
    const handleFollowUpEmailToggle = async (event) => {
        const isEnabled = event.target.checked;

        // Optimistically update UI
        setIsFollowUpEmailEnabled(isEnabled);

        try {
            setSaving(true);
            await updateUserDefaultSettings(memberData.userId, {
                autoFollowUpIntroEmail: isEnabled
            });

            setSnackbar({
                open: true,
                message: isEnabled
                    ? 'Follow-up emails enabled for new cards'
                    : 'Follow-up emails disabled for new cards',
                severity: 'success'
            });
        } catch (error) {
            // Revert on error
            setIsFollowUpEmailEnabled(!isEnabled);
            console.error('[TeamMemberSettingsModal] Error updating follow-up email:', error);
            setSnackbar({
                open: true,
                message: 'Failed to update follow-up email setting',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    // Handler for Lead Capture toggle
    const handleLeadCaptureToggle = async (event) => {
        const isEnabled = event.target.checked;

        // Optimistically update UI
        setIsLeadCaptureEnabled(isEnabled);

        try {
            setSaving(true);
            await updateUserDefaultSettings(memberData.userId, {
                leadCaptureMode: isEnabled
            });

            setSnackbar({
                open: true,
                message: isEnabled
                    ? 'Lead capture enabled for new cards'
                    : 'Lead capture disabled for new cards',
                severity: 'success'
            });
        } catch (error) {
            // Revert on error
            setIsLeadCaptureEnabled(!isEnabled);
            console.error('[TeamMemberSettingsModal] Error updating lead capture:', error);
            setSnackbar({
                open: true,
                message: 'Failed to update lead capture setting',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleNotificationClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <>
            <Modal
                open={open}
                onClose={onClose}
                width={{ xs: '90%', sm: '70%', md: '50%' }}
                height="auto"
            >
                <Box sx={{ padding: 0, position: 'relative' }}>
                    {/* Header */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: { xs: 2, sm: 3 },
                            borderBottom: '1px solid #e0e0e0'
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                fontSize: '1.2rem',
                                color: '#333'
                            }}
                        >
                            Member Settings
                        </Typography>
                        <IconButton
                            onClick={onClose}
                            sx={{
                                color: '#828282',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                        >
                            <CloseOutlinedIcon />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box
                        sx={{
                            padding: { xs: 2, sm: 3, md: 4 },
                            overflowY: 'auto',
                            maxHeight: 'calc(80vh - 80px)'
                        }}
                    >
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                {/* Member Settings Container */}
                                <Box sx={{ width: '100%', padding: { xs: 1, sm: 2 } }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'left',
                                            fontWeight: '500',
                                            marginBottom: 3,
                                            fontSize: { xs: '1rem', sm: '1.2rem' },
                                            color: '#333'
                                        }}
                                    >
                                        Default Settings for New Cards
                                    </Typography>

                                    {/* Follow Up Email Section */}
                                    <Box sx={{ marginTop: 3, marginBottom: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                                    fontWeight: 600,
                                                    color: '#4f4f4f',
                                                }}
                                            >
                                                Follow Up Email
                                            </Typography>
                                            <InfoOutlinedIcon
                                                sx={{
                                                    fontSize: '16px',
                                                    marginLeft: 1,
                                                    color: '#9e9e9e',
                                                    cursor: 'help'
                                                }}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                justifyContent: 'space-between',
                                                width: '100%',
                                                padding: '8px 0',
                                                gap: { xs: 1, sm: 0 }
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                    color: '#828282',
                                                    fontWeight: 600,
                                                    lineHeight: 1.5,
                                                    maxWidth: { xs: '100%', sm: '60%', md: '50%' },
                                                }}
                                            >
                                                Automatically send an email connecting the user of this profile and their new connection upon exchanging info. This will be the default for new cards.
                                            </Typography>

                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {saving && (
                                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                                )}
                                                <TeamIosDarkToggle
                                                    checked={isFollowUpEmailEnabled}
                                                    onChange={handleFollowUpEmailToggle}
                                                    disabled={saving}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ marginY: 2 }} />

                                    {/* Lead Capture Section */}
                                    <Box sx={{ marginTop: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                                    fontWeight: 600,
                                                    color: '#4f4f4f',
                                                }}
                                            >
                                                Lead Capture
                                            </Typography>
                                            <InfoOutlinedIcon
                                                sx={{
                                                    fontSize: '16px',
                                                    marginLeft: 1,
                                                    color: '#9e9e9e',
                                                    cursor: 'help'
                                                }}
                                            />
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                justifyContent: 'space-between',
                                                width: '100%',
                                                padding: '8px 0',
                                                gap: { xs: 1, sm: 0 }
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                    color: '#828282',
                                                    fontWeight: 600,
                                                    lineHeight: 1.5,
                                                    maxWidth: { xs: '100%', sm: '60%', md: '50%' },
                                                }}
                                            >
                                                When lead capture mode is enabled, the lead form will pop up as soon as the profile is shared. This will be the default for new cards.
                                            </Typography>

                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {saving && (
                                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                                )}
                                                <TeamIosDarkToggle
                                                    checked={isLeadCaptureEnabled}
                                                    onChange={handleLeadCaptureToggle}
                                                    disabled={saving}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>
                </Box>
            </Modal>

            {/* Notification Snackbar */}
            <TeamNotificationSnackbar
                open={snackbar.open}
                message={snackbar.message}
                handleClose={handleNotificationClose}
                severity={snackbar.severity}
            />
        </>
    );
};

export default TeamMemberSettingsModal;
